import { designTokens, designTokensDark, type DesignToken, type ThemeName } from '@roomtreatment/design';

/**
 * The theme, for code that cannot read a CSS custom property.
 *
 * DOM and SVG theme for free: every colour they paint is a `var(--rt-*)`, and a
 * `[data-theme]` root swaps those values in one block. Canvas 2D, WebGL and
 * three.js cannot resolve `var()` — `ctx.fillStyle = 'var(--rt-ink)'` silently
 * keeps the previous colour, and `new THREE.Color('var(--x)')` is black — so
 * those consumers ask for the theme NAME and index the TypeScript token set
 * with it. Both paths read one derivation (`surfaces.ts` / `theme-dark.ts`),
 * so they cannot disagree about what "ink" is.
 *
 * The theme is whatever the nearest `[data-theme]` ancestor says — `<html>` in
 * production, any subtree on a design route. So a canvas needs no provider and
 * no prop: it asks its own element. A drawer that is handed a canvas (or a
 * context, via `ctx.canvas`) already has everything it needs.
 */

export const THEME_ATTR = 'data-theme';

export function themeOf(el: Element | null | undefined): ThemeName {
  if (typeof document === 'undefined') return 'light';
  const root = el?.closest(`[${THEME_ATTR}]`) ?? document.documentElement;
  return root.getAttribute(THEME_ATTR) === 'dark' ? 'dark' : 'light';
}

export function tokensFor(theme: ThemeName): Record<DesignToken, string> {
  return theme === 'dark' ? designTokensDark : designTokens;
}

/** The token set in force at an element. Canvas drawers call this with their
 *  own canvas at draw time instead of capturing `designTokens` at import. */
export function tokensAt(el: Element | null | undefined): Record<DesignToken, string> {
  return tokensFor(themeOf(el));
}
